import { GraphInputHelpers } from '@/fetching/graphql/GraphInputHelpers';
import { NameAndCode } from '@/fetching/sharedSchemas/basicSchemas';
import {
    VisitDatetimeField,
    VisitFilter,
    VisitOutcome,
    VisitPriority,
    VisitRequestWorkflowStep,
    VisitState,
    VisitWorkflowStep,
} from '@/types/graphql';
import moment from 'moment';

export { VisitOutcome, VisitPriority, VisitState } from 'gql-types';

export { VisitDatetimeField } from './graphql';

/**
 * A time constraint for comparing datetime fields between related visits.
 * All specified offsets must be satisfied (AND logic).
 */
export type RelativeTimeConstraintInput = {
    /** The datetime field on the related (inner) visit to compare */
    field: VisitDatetimeField;
    /** The datetime field on the outer visit to compare against */
    relativeTo: VisitDatetimeField;
    /** related.field > (outer.relativeTo + offset) */
    gtOffsetMinutes?: number;
    /** related.field >= (outer.relativeTo + offset) */
    gteOffsetMinutes?: number;
    /** related.field < (outer.relativeTo + offset) */
    ltOffsetMinutes?: number;
    /** related.field <= (outer.relativeTo + offset) */
    lteOffsetMinutes?: number;
};

/**
 * Condition for finding related visits to the visit being filtered.
 *
 * This enables filtering visits based on the existence (or non-existence) of
 * other visits that share characteristics with the outer visit and match
 * additional criteria.
 *
 * Example - find visits that need an SFV follow-up (have metadata sfv_required
 * but no confirmed/scheduled SFV visit within 48 hours):
 * {
 *   exists: false,
 *   filter: {
 *     services: { externalIds: { value: { exact: "SFV_SERVICE_ID" } } },
 *     state: { inList: [CONFIRMED, SCHEDULED] }
 *   },
 *   timeConstraints: [{
 *     field: START_DATETIME,
 *     relativeTo: START_DATETIME,
 *     gteOffsetMinutes: 0,
 *     lteOffsetMinutes: 2880  // 48 hours
 *   }],
 *   samePatient: true,
 *   sameEpisode: true
 * }
 */

export const VisitPriorityOrder = {
    [VisitPriority.STANDARD]: 0,
    [VisitPriority.HIGH]: 1,
    [VisitPriority.URGENT]: 2,
};

export const renderVisitPriority = (priority: VisitPriority): string => {
    switch (priority) {
        case VisitPriority.HIGH:
            return 'High';
        case VisitPriority.STANDARD:
            return 'Standard';
        case VisitPriority.URGENT:
            return 'Urgent';
        default:
            return 'Unknown';
    }
};

/**
 * Creates a filter for visits that have an effective date within the given range,
 * without considering visit timeslot preferences. We'll switch this back to
 * a basic effective_date in range filter once
 * @param start - The start date of the range.
 * @param end - The end date of the range.
 * @returns A filter for visits that have an effective date within the given range.
 */
export const createEffectiveDateInRangeFilter = (start: Date, end: Date): VisitFilter => {
    const exclusiveEnd = moment(end).add(1, 'day');
    return {
        startDatetime: {
            range: {
                start: GraphInputHelpers.date(start),
                end: GraphInputHelpers.date(exclusiveEnd),
            },
        },
        OR: {
            startDatetime: {
                isNull: true,
            },
            tentativeDate: {
                range: {
                    start: GraphInputHelpers.date(start),
                    end: GraphInputHelpers.date(end),
                },
            },
            OR: {
                startDatetime: {
                    isNull: true,
                },
                tentativeDate: {
                    isNull: true,
                },
                earliestStartDatetime: {
                    lte: GraphInputHelpers.date(end),
                },
                latestEndDatetime: {
                    gte: GraphInputHelpers.date(start),
                },
            },
        },
    };
};

// Temp: conversion between graphql and zod types
export const visitPriorityToZod = (priority: VisitPriority): NameAndCode => {
    return {
        code: priority,
        name: renderVisitPriority(priority),
    };
};

export const isUrgent = (priority: VisitPriority): boolean => priority === VisitPriority.HIGH;

export const COMPLETE_VISIT_STATES = [VisitState.ENDED, VisitState.IN_REVIEW];
export const VISIT_STATES_THAT_REQUIRE_OUTCOME = [VisitState.ENDED, VisitState.IN_REVIEW];

export const visitStateRequiresOutcome = (state: VisitState): boolean => {
    return VISIT_STATES_THAT_REQUIRE_OUTCOME.includes(state);
};

export const isVisitComplete = (state: VisitState): boolean => {
    return COMPLETE_VISIT_STATES.includes(state);
};

export const renderVisitState = (state: VisitState): string => {
    switch (state) {
        case VisitState.UNSCHEDULED:
            return 'Unscheduled';
        case VisitState.SCHEDULED:
            return 'Scheduled';
        case VisitState.CONFIRMED:
            return 'Confirmed';
        case VisitState.EN_ROUTE:
            return 'En Route';
        case VisitState.AT_LOCATION:
            return 'At Location';
        case VisitState.IN_REVIEW:
            return 'In Review';
        case VisitState.ENDED:
            return 'Ended';
        default:
            return 'Unknown';
    }
};
export const visitStateToZod = (state: VisitState): NameAndCode => {
    return {
        code: state,
        name: renderVisitState(state),
    };
};

export const ReschedulableOutcomes = new Set<VisitOutcome>([
    VisitOutcome.MISSED,
    VisitOutcome.PATIENT_DID_NOT_SHOW,
]);

export const isOutcomeReschedulable = (outcome: VisitOutcome): boolean => {
    return ReschedulableOutcomes.has(outcome);
};

const visitOutcomeToZodCode = (outcome: VisitOutcome): string => {
    switch (outcome) {
        case VisitOutcome.PATIENT_CANCELED:
            return 'CANCELED';
        case VisitOutcome.PATIENT_LATE_CANCELED:
            return 'LATE_CANCELED';
        case VisitOutcome.PATIENT_DID_NOT_SHOW:
            return 'DID_NOT_SHOW';
        default:
            return outcome;
    }
};

export const visitOutcomeFromZodCode = (code: string): VisitOutcome => {
    switch (code) {
        case 'CANCELED':
            return VisitOutcome.PATIENT_CANCELED;
        case 'LATE_CANCELED':
            return VisitOutcome.PATIENT_LATE_CANCELED;
        case 'DID_NOT_SHOW':
            return VisitOutcome.PATIENT_DID_NOT_SHOW;
        default:
            return code as VisitOutcome;
    }
};

export const renderVisitOutcome = (outcome: VisitOutcome): string => {
    switch (outcome) {
        case VisitOutcome.NONE:
            return 'None';
        case VisitOutcome.COMPLETED:
            return 'Completed';
        case VisitOutcome.MISSED:
            return 'Missed';
        case VisitOutcome.PATIENT_CANCELED:
            return 'Canceled';
        case VisitOutcome.PATIENT_LATE_CANCELED:
            return 'Late Canceled';
        case VisitOutcome.PATIENT_DID_NOT_SHOW:
            return 'Patient Did Not Show';
        case VisitOutcome.UNSUCCESSFUL:
            return 'Unsuccessful';
        case VisitOutcome.UNAVAILABLE:
            return 'Unavailable';
        case VisitOutcome.OTHER:
            return 'Other';
        default:
            return 'Unknown';
    }
};

export const visitOutcomeToZod = (outcome: VisitOutcome): NameAndCode => {
    return {
        code: visitOutcomeToZodCode(outcome),
        name: renderVisitOutcome(outcome),
    };
};

/*** Colors ***/
export enum VisitStateAndOutcomeColors {
    GRAY = '#9E9E9E',
    RED = '#F44336',
    ORANGE = '#FDB13E',
    BLUE = '#62BFC1',
}

export const VisitPriorityColors: Record<VisitPriority, string> = {
    [VisitPriority.STANDARD]: '#e7f5f4',
    [VisitPriority.HIGH]: '#b6e0df',
    [VisitPriority.URGENT]: '#83cbc9',
};

export const VisitStateColors: Record<VisitState, string> = {
    [VisitState.UNSCHEDULED]: VisitStateAndOutcomeColors.GRAY,
    [VisitState.SCHEDULED]: VisitStateAndOutcomeColors.GRAY,
    [VisitState.CONFIRMED]: VisitStateAndOutcomeColors.GRAY,
    [VisitState.EN_ROUTE]: VisitStateAndOutcomeColors.ORANGE,
    [VisitState.AT_LOCATION]: VisitStateAndOutcomeColors.ORANGE,
    [VisitState.IN_REVIEW]: VisitStateAndOutcomeColors.ORANGE,
    [VisitState.ENDED]: VisitStateAndOutcomeColors.BLUE,
};

export const VisitOutcomeColors: Record<VisitOutcome, string> = {
    [VisitOutcome.NONE]: VisitStateAndOutcomeColors.GRAY,
    [VisitOutcome.COMPLETED]: VisitStateAndOutcomeColors.BLUE,
    [VisitOutcome.MISSED]: VisitStateAndOutcomeColors.RED,
    [VisitOutcome.PATIENT_CANCELED]: VisitStateAndOutcomeColors.RED,
    [VisitOutcome.PATIENT_LATE_CANCELED]: VisitStateAndOutcomeColors.RED,
    [VisitOutcome.PATIENT_DID_NOT_SHOW]: VisitStateAndOutcomeColors.RED,
    [VisitOutcome.UNSUCCESSFUL]: VisitStateAndOutcomeColors.RED,
    [VisitOutcome.UNAVAILABLE]: VisitStateAndOutcomeColors.RED,
    [VisitOutcome.OTHER]: VisitStateAndOutcomeColors.RED,
};

export const workflowStepFromVisitRequestWorkflowStep = (
    step: VisitRequestWorkflowStep,
): VisitWorkflowStep => {
    switch (step) {
        case VisitRequestWorkflowStep.NEEDS_SERVICES:
            return VisitWorkflowStep.NEEDS_SERVICE;
        case VisitRequestWorkflowStep.NEEDS_CLINICIAN:
            return VisitWorkflowStep.NEEDS_CLINICIAN;
        case VisitRequestWorkflowStep.NEEDS_TENTATIVE_DATE:
            return VisitWorkflowStep.NEEDS_DATE;
        case VisitRequestWorkflowStep.READY_FOR_PROMOTION:
            return VisitWorkflowStep.SCHEDULED;
        case VisitRequestWorkflowStep.COMPLETED:
            return VisitWorkflowStep.SCHEDULED;
        case VisitRequestWorkflowStep.UNKNOWN:
            return VisitWorkflowStep.UNKNOWN;
    }
};
export const workflowStepToInt = (step: VisitWorkflowStep): number => {
    return Object.values(VisitWorkflowStep).indexOf(step);
};

export const renderVisitWorkflowStep = (step: VisitWorkflowStep): string => {
    switch (step) {
        case VisitWorkflowStep.NEEDS_SERVICE:
            return 'Needs Services';
        case VisitWorkflowStep.NEEDS_CLINICIAN:
            return 'Needs Clinician';
        case VisitWorkflowStep.NEEDS_DATE:
            return 'Needs Date';
        case VisitWorkflowStep.NEEDS_TIME:
            return 'Needs Time';
        case VisitWorkflowStep.SCHEDULED:
            return 'Scheduled';
        case VisitWorkflowStep.UNKNOWN:
            return 'Unknown';
        default:
            return step;
    }
};

export const renderVisitWorkflowStepAsAction = (
    step: VisitWorkflowStep,
    plural = false,
): string => {
    switch (step) {
        case VisitWorkflowStep.NEEDS_SERVICE:
            // Plural even when plural is false because individual appointments
            // support multiple services.
            return 'Add Services';
        case VisitWorkflowStep.NEEDS_CLINICIAN:
            return plural ? 'Assign Clinicians' : 'Assign Clinician';
        case VisitWorkflowStep.NEEDS_DATE:
            return plural ? 'Add Dates' : 'Add Date';
        case VisitWorkflowStep.NEEDS_TIME:
            return plural ? 'Add Times' : 'Add Time';
        case VisitWorkflowStep.SCHEDULED:
            return 'Scheduled';
        case VisitWorkflowStep.UNKNOWN:
            return 'Unknown';
        default:
            return step;
    }
};

// Matches visits that don't yet have all of service, clinician, and date assigned.
export const NEEDS_ASSIGNMENT_VISIT_FILTER: VisitFilter = {
    workflowStep: {
        inList: [
            VisitWorkflowStep.NEEDS_SERVICE,
            VisitWorkflowStep.NEEDS_CLINICIAN,
            VisitWorkflowStep.NEEDS_DATE,
        ],
    },
};
